import React, { useContext, useEffect, useState, useCallback, useMemo } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  ChevronDown,
  LayoutDashboard,
  Users,
  ShoppingCart,
  ReceiptIndianRupee,
  FileText,
  MessageSquareText,
  LifeBuoy,
  CreditCard,
  Building2,
  Settings,
  Menu as MenuIcon,
  LogOut,
  User as UserIcon,
} from "lucide-react";
import { GlobalContext } from "../../Context/GlobalContext";
import anlookLogo from "../../Assets/anlook_logo.png";
import anlookLogoEyes from "../../Assets/Title16x16.svg";

/* design tokens */
const BRAND = "#01C37D";
const BORDER = "#EEF2F6";
const TEXT = "#101828";
const TEXT_MUTED = "#667085";
const HOVER_OPEN_BG = "rgba(1,195,125,0.10)";
const CHILD_ACTIVE_BG = "rgba(1,195,125,0.20)";
const CHILD_ACTIVE_BORDER = "#0B7A4F";

const WIDTH_EXPANDED = 232;
const WIDTH_COLLAPSED = 64;

const cx = (...a) => a.filter(Boolean).join(" ");
const normalize = (p) => (p || "").toLowerCase().replace(/\/+$/, "");

/* icons */
function IconByName({ name, className }) {
  const map = {
    dashboard: LayoutDashboard,
    clients: Users,
    orders: ShoppingCart,
    transactions: ReceiptIndianRupee,
    logs: FileText,
    whatsapp: MessageSquareText,
    support: LifeBuoy,
    billing: CreditCard,
    whitelabels: Building2,
    chaafo: Building2,
    settings: Settings,
    default: FileText,
  };
  const Comp = map[name] || map.default;
  return <Comp size={18} className={className} />;
}

/* normalize API menu: clean + dedupe; DO NOT inject self, do NOT remove matches.
   We will APPLY the "skip-first" rule only when rendering (if length > 1). */
function normalizeApiMenu(apiList) {
  const clean = (url) => (url && typeof url === "string" ? url.trim() : "");

  return (apiList || []).map((item) => {
    const title = item?.menu_title || "";
    const parentUrl = clean(item?.menu_url);
    const subsRaw = Array.isArray(item?.submenus) ? item.submenus : [];

    // dedupe by (lower title + normalized url)
    const seen = new Set();
    const submenus = subsRaw
      .map((s) => ({
        id: s?.id ?? `${(s?.submenu_title || "").toLowerCase()}|${normalize(clean(s?.submenu_url))}`,
        submenu_title: (s?.submenu_title || "").trim(),
        submenu_url: clean(s?.submenu_url),
      }))
      .filter((s) => !!s.submenu_url)
      .filter((s) => {
        const key = `${s.submenu_title.toLowerCase()}|${normalize(s.submenu_url)}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

    return {
      id: item?.id || item?.pk_menu_id || title,
      title,
      url: parentUrl,
      iconName: (title || "").toLowerCase().includes("dashboard")
        ? "dashboard"
        : (title || "").toLowerCase().includes("client")
          ? "clients"
          : (title || "").toLowerCase().includes("order")
            ? "orders"
            : (title || "").toLowerCase().includes("transaction") ||
              (title || "").toLowerCase().includes("payout") ||
              (title || "").toLowerCase().includes("payment")
              ? "transactions"
              : (title || "").toLowerCase().includes("log")
                ? "logs"
                : (title || "").toLowerCase().includes("whatsapp") ||
                  (title || "").toLowerCase().includes("chat")
                  ? "whatsapp"
                  : (title || "").toLowerCase().includes("support") ||
                    (title || "").toLowerCase().includes("ticket")
                    ? "support"
                    : (title || "").toLowerCase().includes("billing")
                      ? "billing"
                      : (title || "").toLowerCase().includes("white")
                        ? "whitelabels"
                        : (title || "").toLowerCase().includes("chaafo")
                          ? "chaafo"
                          : (title || "").toLowerCase().includes("setting")
                            ? "settings"
                            : "default",
      submenus, // raw cleaned submenus (no injection/removal)
    };
  });
}

/* data hook */
function useAdminMenu() {
  const { api, setUserAccess } = useContext(GlobalContext) || {};
  const [loading, setLoading] = useState(true);
  const [menu, setMenu] = useState([]);
  const [error, setError] = useState("");


  //  Old API 
  // const userId = useMemo(() => {
  //   try {
  //     const raw = sessionStorage.getItem("userData");
  //     const ss = raw ? JSON.parse(raw) : null;
  //     return ss?.adminMeta?.portalId ? String(ss.adminMeta.portalId) : "";
  //   } catch {
  //     return "";
  //   }
  // }, []);
  // const fetchMenu = useCallback(async () => {
  //   if (!api || !userId) {
  //     setLoading(false);
  //     return;
  //   }
  //   setLoading(true);
  //   setError("");
  //   try {
  //     const res = await api.post(`/Admin/getUserAccessV1`, { userid: userId });
  //     if (res?.data?.responseCode === "00") {
  //       setMenu(normalizeApiMenu(res.data.response || []));
  //     } else {
  //       setError(res?.data?.responseMessage || "Failed to load navigation.");
  //     }
  //   } catch (e) {
  //     setError("Failed to load navigation.");
  //     console.error("Sidebar menu fetch error", e);
  //   } finally {
  //     setLoading(false);
  //   }
  // }, [api, userId]);

  // New API endpoint as per latest backend changes

  const userId = useMemo(() => {
    try {
      const raw = sessionStorage.getItem("userData");
      const ss = raw ? JSON.parse(raw) : null;
      return ss?.adminMeta?.adminId ? String(ss.adminMeta.adminId) : "";
    } catch {
      return "";
    }
  }, []);
  const fetchMenu = useCallback(async () => {
    if (!api || !userId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await api.post(`/Admin/getDataViaAdminID`, {
        admin_id: userId,
      });

      if (res?.data?.responseCode === "00") {

        const normalized = normalizeApiMenu(
          res.data.response || []
        );

        const allowedRoutes = normalized.flatMap((menu) => [
          menu.url,
          ...(menu.submenus || []).map(
            (s) => s.submenu_url
          ),
        ]);

        setMenu(normalized);

        setUserAccess(
          allowedRoutes.map((r) =>
            r?.toLowerCase()?.replace(/\/+$/, "")
          )
        );
      }
      else {
        setError(res?.data?.responseMessage || "Failed to load navigation.");
      }
    } catch (e) {
      setError("Failed to load navigation.");
      console.error("Sidebar menu fetch error", e);
    } finally {
      setLoading(false);
    }
  }, [api, userId, setUserAccess]);

  useEffect(() => { fetchMenu(); }, []);
  return { loading, error, menu, hasUser: !!userId };
}

/* role color map */
function roleColors(userType = "") {
  const r = String(userType).toLowerCase();
  if (r.includes("super")) return { bg: "#FEE2E2", text: "#B91C1C", ring: "#FCA5A5" };
  if (r.includes("manager")) return { bg: "#DBEAFE", text: "#1E40AF", ring: "#93C5FD" };
  if (r.includes("admin")) return { bg: "#DCFCE7", text: "#065F46", ring: "#86EFAC" };
  return { bg: "#F3F4F6", text: "#374151", ring: "#E5E7EB" };
}

export default function Sidebar() {
  const { loading, error, menu, hasUser } = useAdminMenu();
  const location = useLocation();
  const navigate = useNavigate();
  const path = normalize(location.pathname);

  /* collapsed / expanded */
  const [collapsed, setCollapsed] = useState(() => {
    try { return JSON.parse(localStorage.getItem("nav.collapsed") || "false"); } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("nav.collapsed", JSON.stringify(collapsed)); } catch { }
  }, [collapsed]);

  /* single open group */
  const [expandedId, setExpandedId] = useState(null);

  /* admin card meta (uses userType) */
  const { adminName, userType } = useMemo(() => {
    try {
      const raw = sessionStorage.getItem("userData");
      const ss = raw ? JSON.parse(raw) : null;
      const name = ss?.adminMeta?.adminName || ss?.adminMeta?.name || "Admin";
      const type = ss?.userType || ss?.adminMeta?.userType || "Admin";
      return { adminName: name, userType: type };
    } catch {
      return { adminName: "Admin", userType: "Admin" };
    }
  }, []);
  const rc = roleColors(userType);

  /* find route group (parent or any visible child) */
 const routeGroup = useMemo(() => {
  for (const g of menu) {
    const submenus = Array.isArray(g.submenus)
      ? g.submenus
      : [];

    if (normalize(g.url) === path) {
      return {
        group: g,
        viaChild: false,
      };
    }

    const childIsActive = submenus.some((submenu) => {
      const childPath = normalize(submenu.submenu_url);

      return (
        childPath === path ||
        path.startsWith(`${childPath}/`)
      );
    });

    if (childIsActive) {
      return {
        group: g,
        viaChild: true,
      };
    }
  }

  return {
    group: null,
    viaChild: false,
  };
}, [menu, path]);

  /* parent is active if exact parent URL OR any visible child is active */
 const parentIsActive = (g) => {
  const parentPath = normalize(g.url);

  const submenus = Array.isArray(g.submenus)
    ? g.submenus
    : [];

  const childIsActive = submenus.some((submenu) => {
    const childPath = normalize(submenu.submenu_url);

    return (
      childPath === path ||
      path.startsWith(`${childPath}/`)
    );
  });

  return (
    parentPath === path ||
    childIsActive
  );
};

  /* keep only current group open */
useEffect(() => {
  if (collapsed) return;

  const currentGroup = routeGroup.group;

  if (!currentGroup) {
    setExpandedId(null);
    return;
  }

  const hasDropdown =
    Array.isArray(currentGroup.submenus) &&
    currentGroup.submenus.length > 1;

  setExpandedId(
    hasDropdown ? currentGroup.id : null,
  );
}, [routeGroup.group, collapsed]);

  /* parent click:
     - if has dropdown: expand + go to FIRST VISIBLE child (submenus[1])
     - else: go to parent url (single item) */
  const onParentClick = (g) => {
    const hasDropdown = (g.submenus || []).length > 1;
    if (hasDropdown) {
      const visible = g.submenus.slice(0);
      if (!collapsed) setExpandedId(g.id);
      if (visible[0]?.submenu_url) navigate(visible[0].submenu_url);

    } else if (g.url) {
      navigate(g.url);
    }
  };

  /* logout */
  const handleLogout = () => {
    const ok = window.confirm("Are you sure you want to log out?");
    if (!ok) return;
    try { sessionStorage.removeItem("userData"); } catch { }
    window.location.href = "https://admin.anlook.com/login";
  };

  /* single (non-dropdown) row */
  const SingleRow = ({ g }) => {
    const isActive = normalize(g.url) === path;
    return (
      <NavLink
        to={g.url}
        end
        className="group flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium border transition-colors"
        style={{
          backgroundColor: isActive ? BRAND : "white",
          borderColor: isActive ? BRAND : BORDER,
          color: isActive ? "#FFFFFF" : TEXT,
        }}
        onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = HOVER_OPEN_BG; }}
        onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = "white"; }}
        title={g.title}
      >
        <IconByName name={g.iconName} className={isActive ? "text-white" : "text-[#01C37D]"} />
        {!collapsed && <span className="truncate">{g.title}</span>}
      </NavLink>
    );
  };

  /* parent row (dropdown) */
  const ParentRow = ({ g }) => {
    const active = parentIsActive(g);
    const open = !collapsed && expandedId === g.id;
    return (
      <button
        type="button"
        onClick={() => onParentClick(g)}
        className="group w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium border transition-colors"
        style={{
          backgroundColor: active ? BRAND : "white",
          borderColor: active ? BRAND : BORDER,
          color: active ? "#FFFFFF" : TEXT,
        }}
        onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = HOVER_OPEN_BG; }}
        onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = "white"; }}
        title={g.title}
      >
        <IconByName name={g.iconName} className={active ? "text-white" : "text-[#01C37D]"} />
        {!collapsed && (
          <>
            <span className="flex-1 text-left truncate">{g.title}</span>
            <ChevronDown
              size={16}
              className={cx("transition-transform", open ? "rotate-180" : "")}
              style={{ color: active ? "#FFFFFF" : TEXT_MUTED }}
            />
          </>
        )}
      </button>
    );
  };

  /* child link (center text + LEFT green border on active) */
  const ChildLink = ({ s }) => (
    <NavLink
      to={s.submenu_url}
      end
      className="block px-3 py-1.5 rounded-md text-sm transition-colors text-start"
      style={({ isActive }) => ({
        backgroundColor: isActive ? CHILD_ACTIVE_BG : "transparent",
        color: isActive ? "#0B3B2C" : TEXT,
        borderLeft: isActive ? `3px solid ${CHILD_ACTIVE_BORDER}` : "3px solid transparent",
      })}
      title={s.submenu_title || "Untitled"}
    >
      {s.submenu_title || "Untitled"}
    </NavLink>
  );

  return (
    <aside
      className="sticky top-0 h-screen bg-white border-r flex flex-col"
      style={{ width: collapsed ? WIDTH_COLLAPSED : WIDTH_EXPANDED, borderColor: BORDER }}
    >
      {/* Brand row */}
      <div className="h-16 flex items-center justify-between px-3 border-b" style={{ borderColor: BORDER }}>
        <div className={cx("flex items-center", collapsed ? "justify-center w-full" : "gap-2")}>
          {collapsed ? (
            <button
              aria-label="Expand sidebar"
              className="h-9 w-9 rounded-lg flex items-center justify-center font-bold"
              style={{ backgroundColor: BRAND, color: "#fff" }}
              onClick={() => setCollapsed(false)}
              title="Expand"
            >
              <img src={anlookLogoEyes} alt="A" />
            </button>
          ) : (
            <>
              <img src={anlookLogo} alt="Anlook" className="h-7 w-auto" />
            </>
          )}
        </div>

        {/* collapse control (expanded only) */}
        {!collapsed && (
          <button
            className="p-2 rounded hover:bg-gray-50"
            onClick={() => setCollapsed(true)}
            title="Collapse"
          >
            <MenuIcon size={18} />
          </button>
        )}
      </div>

      {/* Admin card (hidden when collapsed) */}
      {!collapsed && (
        <div className="px-3 pt-3">
          <div
            className="flex items-center gap-3 rounded-xl border px-3 py-2"
            style={{ backgroundColor: rc.bg, borderColor: rc.ring, color: rc.text }}
          >
            <div
              className="h-9 w-9 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: "#FFFFFF", border: `1px solid ${rc.ring}` }}
            >
              <UserIcon size={18} className="text-gray-600" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate">{adminName}</div>
              <div className="text-xs opacity-80 truncate">{userType}</div>
            </div>
          </div>
        </div>
      )}

      {/* Menu */}
      <div className="flex-1 overflow-y-auto p-2">
        {!hasUser && <div className="text-xs" style={{ color: TEXT_MUTED }}>Loading user context…</div>}
        {hasUser && loading && (
          <div className="space-y-2 mt-2">
            <div className="h-9 rounded bg-gray-100 animate-pulse" />
            <div className="h-9 rounded bg-gray-100 animate-pulse" />
            <div className="h-9 rounded bg-gray-100 animate-pulse" />
          </div>
        )}
        {hasUser && !loading && error && (
          <div className="text-sm text-red-600 mt-2">{error}</div>
        )}

        {hasUser && !loading && !error && (
          <ul className="space-y-1 mt-2">
            {menu.map((g) => {
              const all = g.submenus || [];
              const hasDropdown = all.length > 1;
              const visibleChildren = hasDropdown ? all.slice(0) : []; // ✅ skip first if dropdown
              const open = !collapsed && expandedId === g.id;

              return (
                <li key={g.id} className="mb-0.5">
                  {hasDropdown ? (
                    <>
                      <ParentRow g={g} />
                      {open && (
                        <ul className="mt-1 ml-6 space-y-1">
                          {visibleChildren.map((s, i) => (
                            <li key={`${g.id}-${s.id || s.submenu_url || i}`}>
                              <ChildLink s={s} />
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  ) : (
                    // single item (no dropdown)
                    <SingleRow g={g} />
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Logout */}
      <div className="p-2 border-t" style={{ borderColor: BORDER }}>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
          style={{ backgroundColor: "white", border: `1px solid ${BORDER}`, color: TEXT }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = HOVER_OPEN_BG)}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "white")}
          title="Logout"
        >
          <LogOut size={18} className="text-[#ef4444]" />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </aside>
  );
}
